import React from 'react';
import { Icon } from '@iconify/react';
import { useGetAgentHisotryMutation, useTypedSelector } from '@circuitry-ai/doc-data';
import { CtyButton, ErrorBoundary } from '@circuitry-ai/doc-ui';
import { Card, CardHeader, CardBody, CardFooter, Divider } from '@nextui-org/react';

interface GenieUsageRecord {
  aiworker_usage_id: string;
  aiworker_id: string;
  used_at?: string;
  date?: string;
  timestamp?: string;
  status: string;
  user_name?: string;
  user_id?: string;
  input_data?: { user_input?: string; original_data?: string };
  output_data?: { generated_code?: string; action_name?: string; saved_at?: string };
  action_output?: { generated_code?: string; action_name?: string; saved_at?: string };
}

export interface TemplatePayload {
  component: string;
  formConfig: Record<string, any> | null;
  toolUsed: string | null;
}

interface GenieTemplatesProps {
  onOpenTemplate: (data: TemplatePayload) => void;
}

const getOutputData = (r: GenieUsageRecord) => r.action_output ?? r.output_data;

const getTemplateName = (r: GenieUsageRecord): string =>
  getOutputData(r)?.action_name ||
  r.input_data?.user_input ||
  'Untitled Template';

const getTemplateDate = (r: GenieUsageRecord): string => {
  const out = getOutputData(r);
  const raw = r.date ?? r.used_at ?? r.timestamp ?? out?.saved_at;
  if (!raw) return '';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// Reconstruct component + formConfig from the stored API output (original_data
// holds the raw JSON that came back from the /run endpoint).
const parseTemplatePayload = (r: GenieUsageRecord): TemplatePayload => {
  try {
    const raw = r.input_data?.original_data;
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, any>;
      return {
        component: String(parsed.component ?? getOutputData(r)?.generated_code ?? ''),
        formConfig: (parsed.formConfig as Record<string, any> | null) ?? null,
        toolUsed: null,
      };
    }
  } catch { /* fall through */ }
  return {
    component: String(getOutputData(r)?.generated_code ?? ''),
    formConfig: null,
    toolUsed: null,
  };
};

const TemplateCard: React.FC<{
  record: GenieUsageRecord;
  onOpen: (data: TemplatePayload) => void;
}> = ({ record, onOpen }) => {
  const name = getTemplateName(record);
  const date = getTemplateDate(record);
  const hasFormConfig = (() => {
    try {
      const raw = record.input_data?.original_data;
      if (!raw) return false;
      const p = JSON.parse(raw);
      return Boolean(p.formConfig);
    } catch { return false; }
  })();

  return (
    <Card className="border shadow-sm w-full" shadow="none">
      <CardHeader className="flex items-start gap-3 pb-2">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#FFF4E8]">
          <Icon icon="mdi:creation-outline" className="text-[#FF9F1C]" width={18} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-foreground" title={name}>
            {name}
          </p>
          {date && (
            <p className="mt-0.5 text-[11px] text-default-500">{date}</p>
          )}
        </div>
      </CardHeader>

      <CardBody className="pt-0 pb-2">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
            hasFormConfig
              ? 'bg-blue-50 text-blue-700'
              : 'bg-[#FFF4E8] text-[#B45309]'
          }`}
        >
          <Icon
            icon={hasFormConfig ? 'mdi:form-select' : 'mdi:view-dashboard-outline'}
            width={11}
          />
          {hasFormConfig ? 'Form' : 'UI Component'}
        </span>
      </CardBody>

      <Divider />

      <CardFooter className="pt-2">
        <CtyButton
          className="w-full"
          variant="solid"
          radius="sm"
          size="sm"
          color="primary"
          onPress={() => onOpen(parseTemplatePayload(record))}
        >
          <Icon icon="mdi:open-in-app" width={14} className="mr-1" />
          Open in Chat
        </CtyButton>
      </CardFooter>
    </Card>
  );
};

const TemplateCardWithErrorBoundary = ErrorBoundary(
  TemplateCard as unknown as React.ComponentType<Record<string, unknown>>
);

export function GenieTemplates({ onOpenTemplate }: GenieTemplatesProps) {
  const token = useTypedSelector((state) => state.auth.token);
  const [records, setRecords] = React.useState<GenieUsageRecord[]>([]);
  const [getUsageHistory, { isLoading }] = useGetAgentHisotryMutation();

  React.useEffect(() => {
    if (!token) return;
    getUsageHistory({
      pagination: { page: 1, per_page: 50 },
      filters: [{ aiworker_type: 'genie_logs', condition: 'contains' }],
    })
      .unwrap()
      .then((res: any) => {
        let data: GenieUsageRecord[] = [];
        if (Array.isArray(res?.aiworkers_conversations)) data = res.aiworkers_conversations;
        else if (Array.isArray(res?.data?.aiworkers_conversations)) data = res.data.aiworkers_conversations;
        else if (Array.isArray(res?.data)) data = res.data;
        else if (Array.isArray(res)) data = res;
        setRecords(data.sort((a, b) => {
          const da = new Date(a.date ?? a.used_at ?? 0).getTime();
          const db = new Date(b.date ?? b.used_at ?? 0).getTime();
          return db - da;
        }));
      })
      .catch(() => setRecords([]));
  }, [token, getUsageHistory]);

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Icon icon="eos-icons:loading" className="animate-spin text-[#FF9F1C]" width={40} />
          <span className="text-[12px] text-default-500">Loading templates…</span>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F2F4F7] text-default-600">
          <Icon icon="mdi:creation-outline" width={28} />
        </div>
        <p className="text-[13px] font-semibold text-foreground">No saved templates yet</p>
        <p className="mt-2 max-w-xs text-[12px] leading-5 text-default-500">
          Generate a component in the Chat tab and save it — it will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {records.map((record) => (
          <TemplateCardWithErrorBoundary
            key={record.aiworker_usage_id}
            record={record as any}
            onOpen={onOpenTemplate as any}
          />
        ))}
      </div>
    </div>
  );
}

export default GenieTemplates;
